import { Button, Caption1, MessageBar, MessageBarBody, MessageBarTitle, Text, makeStyles, mergeClasses,
} from '@fluentui/react-components';
import {
  fileCategoryLabels, offerSigningFields, signedOfferStage,
  type OfferRecord, type StepInstance,
} from '@vigeo/shared';
import { ShellIcons, StatusDot, complianceTokenFor, tokens } from '@vigeo/ui';
import { StoredFileLink } from '@/features/files';
import { dayAndTime } from '@/features/offers';
import { reviewChecks, reviewCopy, reviewLede, reviewNotice, offerTrackerSteps, sendBackHint, signedStageBadge } from './model.js';
import { OfferTracker } from './OfferTracker.js';

const useStyles = makeStyles({
  card: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  intro: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
  },
  crumb: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalXXS, flexWrap: 'wrap' },
  crumbSection: { color: tokens.colorBrandForeground1 },
  crumbLeaf: { fontWeight: tokens.fontWeightSemibold },
  h: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightSemibold },
  tracker: { paddingInline: tokens.spacingHorizontalXL, paddingBottom: tokens.spacingVerticalL },
  /**
   * The frame's two columns. `flexWrap` plus a real basis rather than a media query: the side
   * panel is a fixed 17rem beside a document that wants every remaining pixel, so the point at
   * which they must stack is decided by the panel's own width, not by the viewport's.
   */
  body: {
    display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXL,
    paddingInline: tokens.spacingHorizontalXL, paddingBottom: tokens.spacingVerticalXL,
  },
  document: {
    display: 'flex', flexDirection: 'column', minWidth: 0, flexBasis: '22rem', flexGrow: 1,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalS, minWidth: 0,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  fileName: { flexGrow: 1, minWidth: 0, fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere' },
  icon: { display: 'flex', flexDirection: 'row', alignItems: 'center', color: tokens.colorNeutralForeground2 },
  pages: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, padding: tokens.spacingHorizontalM },
  /** The signing page itself, drawn as a page rather than a form: this is a record, not an input. */
  page: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalXL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  filled: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  filledValue: {
    paddingBlock: tokens.spacingVerticalXS,
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold,
  },
  tick: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalS },
  side: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, flexBasis: '17rem', flexGrow: 1, minWidth: 0 },
  field: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  label: { color: tokens.colorNeutralForeground2 },
  checks: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  check: { display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: tokens.spacingHorizontalXS },
  checkMet: { color: tokens.colorComplianceGood },
  checkFailed: { color: tokens.colorComplianceCritical },
  checkText: { flexGrow: 1, minWidth: 0 },
  actions: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
});

export interface ReviewSignedOfferProps {
  readonly record: OfferRecord;
  readonly step: StepInstance;
  readonly firstName: string;
  /** Write actions are HR_Admin only; the API enforces it too. */
  readonly canEdit: boolean;
  readonly busy: boolean;
  readonly onApprove: () => void;
  /** Opens the reason dialog. Sending back without one is refused by the API. */
  readonly onSendBack: () => void;
}

/**
 * Row 2.1 — reviewing the offer a candidate has signed (frame 06b `321:1066`, issue #263).
 *
 * WHAT THIS SCREEN CANNOT YET DO, said here because it is the frame's largest element. The left
 * pane is meant to be the signed PDF with page thumbnails. Those bytes are a STAGED UPLOAD until
 * HR approves the step — `onApprove` is what emits the `store-file` effect — and the only route
 * that serves document bytes to this app, `people/{personId}/files/{fileId}/content`, reads the
 * records store and therefore only ever sees a file that has already been filed. There is no
 * route that serves a submitted-but-unfiled artifact, so in the one state this row exists for the
 * PDF genuinely cannot be fetched. Adding one is a new authenticated read over candidate-supplied
 * bytes whose malware-scan state matters, which belongs in its own issue with its own review.
 *
 * So the pane renders the signing page's CONTENT from the submission instead — the acceptance
 * sentence, the tick, the typed name and the signing date, which is every value the frame's own
 * page-4 preview shows. That is what HR is actually checking. The pixels are named as missing
 * rather than faked, and #263's "approving requires the document to have been rendered" is
 * NOT met: see the PR body.
 */
export function ReviewSignedOffer({ record, step, firstName, canEdit, busy, onApprove, onSendBack }: ReviewSignedOfferProps) {
  const styles = useStyles();
  const stage = signedOfferStage(step);
  const badge = stage === null ? null : signedStageBadge[stage];

  const submission = (step.submission ?? {}) as { values?: Record<string, unknown>; fileName?: string };
  const values = submission.values ?? {};
  const deadline = record.terms.acceptanceDeadline;
  const checks = reviewChecks(step.submittedAt, deadline, deadline === null ? null : dayAndTime(deadline), values);

  const typedName = typeof values['signature'] === 'string' ? values['signature'] : null;
  const signedDate = typeof values['signedDate'] === 'string' ? values['signedDate'] : null;
  const accepted = values['acceptance'] === true;
  // The offer's OWN target, not the agency default: the default may have been changed since this
  // offer was drafted, and what matters is where this signed copy is going.
  const target = record.terms.target;

  return (
    <section className={styles.card} aria-label={reviewCopy.title}>
      <div className={styles.intro}>
        <div className={styles.crumb}>
          <Caption1 className={styles.crumbSection}>{reviewCopy.crumbSection}</Caption1>
          <Caption1 aria-hidden="true">›</Caption1>
          <Caption1 className={styles.crumbLeaf}>{reviewCopy.crumbLeaf}</Caption1>
        </div>
        <h3 className={styles.h}>{reviewCopy.title}</h3>
        <Text>{reviewLede(firstName)}</Text>
      </div>

      <div className={styles.tracker}>
        {/* Every stage is behind them: the candidate has signed, which is the last one. */}
        <OfferTracker steps={offerTrackerSteps} reached={offerTrackerSteps.length - 1} />
      </div>

      <div className={styles.body}>
        <div className={styles.document}>
          <div className={styles.toolbar}>
            <span className={styles.icon}><ShellIcons.document /></span>
            <Text className={styles.fileName}>{submission.fileName ?? 'Signed offer'}</Text>
            <StoredFileLink personId={step.personId} storedFileId={step.storedFileId} legacyUrl={step.sharePointItemUrl} label="Open" />
          </div>
          <div className={styles.pages}>
            <div className={styles.page}>
              <Caption1>Signing page</Caption1>
              <Text>{reviewCopy.acceptanceSentence}</Text>
              <div className={styles.tick}>
                <span className={mergeClasses(styles.icon, accepted ? styles.checkMet : styles.checkFailed)}>
                  {accepted ? <ShellIcons.check /> : <ShellIcons.close />}
                </span>
                <Text>{offerSigningFields[0]!.label}</Text>
              </div>
              <div className={styles.filled}>
                <Text>{offerSigningFields[1]!.label}:</Text>
                <Text className={styles.filledValue}>{typedName ?? 'Not signed'}</Text>
              </div>
              <div className={styles.filled}>
                <Text>{offerSigningFields[2]!.label}:</Text>
                <Text className={styles.filledValue}>{signedDate ?? 'Not dated'}</Text>
              </div>
            </div>
            {/*
              Said plainly rather than left to be inferred from an empty frame. A reviewer who
              believes they are looking at the document would approve on the strength of it.
            */}
            <Caption1>
              These are the values {firstName} submitted. The signed PDF itself can only be opened once it is filed.
            </Caption1>
          </div>
        </div>

        <div className={styles.side}>
          <div className={styles.field}>
            <Caption1 className={styles.label}>{reviewCopy.statusLabel}</Caption1>
            {badge === null ? null : (
              <div><StatusDot color={badge.tone === 'good' ? complianceTokenFor.good : complianceTokenFor.warning}>{badge.label}</StatusDot></div>
            )}
          </div>

          {step.submittedAt === null ? null : (
            <div className={styles.field}>
              <Caption1 className={styles.label}>{reviewCopy.signedLabel}</Caption1>
              <Text>{dayAndTime(step.submittedAt)}</Text>
            </div>
          )}

          <div className={styles.checks}>
            {checks.map((check) => (
              <div key={check.key} className={styles.check}>
                <span className={mergeClasses(styles.icon, check.met === true ? styles.checkMet : check.met === false ? styles.checkFailed : undefined)}>
                  {check.met === false ? <ShellIcons.close /> : <ShellIcons.check />}
                </span>
                <Text className={styles.checkText}>{check.label}</Text>
              </div>
            ))}
          </div>

          <div className={styles.field}>
            <Caption1 className={styles.label}>{reviewCopy.typedNameLabel}</Caption1>
            <Text>{typedName ?? '—'}</Text>
          </div>

          <div className={styles.field}>
            <Caption1 className={styles.label}>{reviewCopy.filesToLabel}</Caption1>
            <Text>{fileCategoryLabels[target]}</Text>
          </div>

          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>{reviewCopy.noticeTitle}</MessageBarTitle>
              {reviewNotice(firstName)}
            </MessageBarBody>
          </MessageBar>

          {/*
            A Viewer is offered nothing rather than a disabled stand-in, the same rule the Offers
            list keeps: both actions need `review.decide`, which they do not have.
          */}
          {canEdit && stage === 'awaiting-hr' ? (
            <div className={styles.actions}>
              <Button appearance="primary" disabled={busy} onClick={onApprove}>{reviewCopy.approve}</Button>
              <Button disabled={busy} onClick={onSendBack}>{reviewCopy.sendBack}</Button>
              <Caption1>{sendBackHint(firstName)}</Caption1>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
